import { useState } from "react";
import { Pencil } from "lucide-react";
import { Input } from "@/components/ui/input";
import { toast } from "@/components/ui/use-toast";
import { extractErrorMessage } from "@/api/client";
import { useUpdateMaterial } from "@/api/hooks/useCennik";
import type { Material } from "@/api/schemas";
import { formatEur, formatPct, normalizeNum, parseDecimal } from "@/lib/format";
import { cn } from "@/lib/utils";

/** Ceny, ktoré sa dajú meniť priamo v tabuľke Cenníka. */
export type CenovePole = "nakup_cena" | "rezia_pct" | "praca_eur_per_mj";

type Pravidlo = {
  /** Akuzatív — „Upraviť réžiu". */
  nazov: string;
  /** Nadpis stĺpca pre čítačku — „Réžia 20 %". */
  popis: string;
  /** Desatinné miesta, ktoré pustí server (`MaterialUpdateRequest`). */
  desatinne: number;
  max?: number;
  format: (v: string | number | null | undefined) => string;
};

const PRAVIDLA: Record<CenovePole, Pravidlo> = {
  nakup_cena: { nazov: "nákupnú cenu", popis: "Cena", desatinne: 4, format: formatEur },
  rezia_pct: { nazov: "réžiu", popis: "Réžia", desatinne: 2, max: 100, format: formatPct },
  praca_eur_per_mj: { nazov: "prácu", popis: "Práca", desatinne: 4, format: formatEur },
};

/**
 * Overenie čísla ≥ 0 — vráti ho s bodkou, alebo text chyby. Desatinné
 * miesta a strop podľa servera, aby user nedostal anglickú 422. Používa
 * ho bunka aj hromadná úprava marží.
 */
export function overCislo(
  vstup: string,
  pravidlo: { desatinne: number; max?: number },
): { hodnota: string } | { chyba: string } {
  const cisto = parseDecimal(vstup);
  if (!/^\d+(\.\d+)?$/.test(cisto)) {
    return { chyba: "Zadaj číslo 0 alebo viac (desatinná čiarka aj bodka)." };
  }
  const desatinne = cisto.split(".")[1]?.length ?? 0;
  if (desatinne > pravidlo.desatinne) {
    return { chyba: `Najviac ${pravidlo.desatinne} desatinné miesta.` };
  }
  if (pravidlo.max !== undefined && Number(cisto) > pravidlo.max) {
    return { chyba: `Najviac ${pravidlo.max}.` };
  }
  return { hodnota: cisto };
}

/** Overenie hodnoty bunky — pravidlá poľa z `PRAVIDLA`. */
export function overCenu(
  pole: CenovePole,
  vstup: string,
): { hodnota: string } | { chyba: string } {
  return overCislo(vstup, PRAVIDLA[pole]);
}

function popisBunky(material: Material, pole: CenovePole): string {
  const ovladanie = "Klik = upraviť priamo v tabuľke (Enter uloží, Esc zruší).";
  if (pole === "nakup_cena") {
    return material.dodavatel
      ? `${ovladanie}\nNákupnú cenu prepíše ďalší import cenníka dodávateľa.`
      : ovladanie;
  }
  return material.marze_rucne
    ? `${ovladanie}\nVlastná marža — import cenníka ani Nastavenia cien ju neprepíšu.`
    : `${ovladanie}\nIná hodnota než má kategória urobí z položky vlastnú maržu — import cenníka ani Nastavenia cien ju potom neprepíšu.`;
}

/**
 * Nákup, réžia alebo práca priamo v riadku Cenníka (KOLO 166).
 *
 * Klik otvorí pole (riadok pritom neotvorí okno položky), **Enter uloží,
 * Esc aj klik vedľa zruší** — naopak než pri postupoch v Operáciách:
 * náhodný klik mimo nesmie zmeniť cenu. Rovnaká hodnota sa neposiela.
 *
 * Či je réžia/práca „vlastná marža", rozhoduje server porovnaním
 * s kategóriou (`services/marze_kategorii`); bunka mu pošle len jedno
 * pole. Predošlú nákupnú cenu zapíše listener v backende.
 */
export function CenaBunka({
  material,
  pole,
  lenCitanie = false,
}: {
  material: Material;
  pole: CenovePole;
  /** Bez práva na sekciu (KOLO 231) — len zobrazí. */
  lenCitanie?: boolean;
}): JSX.Element {
  const pravidlo = PRAVIDLA[pole];
  const aktualna = material[pole];
  const update = useUpdateMaterial();
  const [koncept, setKoncept] = useState<string | null>(null);
  const zobrazena = pravidlo.format(aktualna);
  const vlastnaMarza = pole !== "nakup_cena" && material.marze_rucne === true;

  // Set má cenu zo súčtu komponentov (alebo ručnú v okne setu)
  if ((pole === "nakup_cena" && material.je_set) || lenCitanie) {
    return <span>{zobrazena}</span>;
  }

  async function uloz(): Promise<void> {
    if (koncept === null || update.isPending) return;
    const vysledok = overCenu(pole, koncept);
    if ("chyba" in vysledok) {
      toast({
        title: "Hodnota sa neuložila",
        description: vysledok.chyba,
        variant: "destructive",
      });
      return;
    }
    if (Number(vysledok.hodnota) === Number(aktualna)) {
      setKoncept(null);
      return;
    }
    try {
      const { data } = await update.mutateAsync({
        id: material.id,
        data: { [pole]: vysledok.hodnota },
      });
      setKoncept(null);
      if (pole !== "nakup_cena" && data.marze_rucne !== material.marze_rucne) {
        toast(
          data.marze_rucne
            ? {
                title: "Položka má vlastnú maržu",
                description: `${material.nazov} — import cenníka ani Nastavenia cien ju neprepíšu.`,
              }
            : {
                title: "Marža ako kategória",
                description: `${material.nazov} sa znova riadi Nastaveniami cien.`,
              },
        );
      }
    } catch (err) {
      toast({
        title: "Hodnota sa neuložila",
        description: extractErrorMessage(err),
        variant: "destructive",
      });
    }
  }

  if (koncept !== null) {
    return (
      <Input
        autoFocus
        inputMode="decimal"
        value={koncept}
        readOnly={update.isPending}
        aria-label={`Upraviť ${pravidlo.nazov} — ${material.nazov}`}
        onChange={(e) => setKoncept(e.target.value)}
        // Riadok pod bunkou otvára okno položky
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            void uloz();
          } else if (e.key === "Escape") {
            e.preventDefault();
            setKoncept(null);
          }
        }}
        // Klik vedľa zruší — rozpísaná cena sa neuloží nedopatrením
        onBlur={() => {
          if (!update.isPending) setKoncept(null);
        }}
        onFocus={(e) => e.currentTarget.select()}
        // Šírka podľa stĺpca — pevných 96 px pretekalo z úzkej Réžie
        className="h-7 w-full min-w-0 max-w-[6rem] px-1.5 text-right font-mono text-xs"
      />
    );
  }

  return (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        // Po slovensky s čiarkou; späť na bodku ju vráti `overCenu`
        setKoncept(normalizeNum(aktualna).replace(".", ","));
      }}
      title={popisBunky(material, pole)}
      aria-label={`${pravidlo.popis} ${zobrazena} — upraviť (${material.nazov})`}
      className={cn(
        "group relative inline-flex items-center justify-end rounded px-1 hover:bg-muted",
        vlastnaMarza && "text-primary",
      )}
    >
      {/* Ceruzka mimo toku — inak zaberá 16 px v každej bunke aj bez
          myši a úzke stĺpce Cenníka pretekali (KOLO 199) */}
      <Pencil
        className="pointer-events-none absolute right-full top-1/2 mr-0.5 h-3 w-3 -translate-y-1/2 opacity-0 group-hover:opacity-50"
        aria-hidden
      />
      {zobrazena}
    </button>
  );
}
